import React, { useState } from 'react';
import { Button } from '@material-ui/core';

import DocumentView from '@root/components/DocumentView';
import GenerateButtonWithCap from '@root/views/File/components/GenerateButtonWithCap';
import Notification from '@root/components/shared/alerts/Notification';
import DocumentControls from '@root/views/File/components/DocumentControls';
import { LandingPageExporter, downloadAsFile, makeFileName } from '@root/utils/Export';

import PreviewButton from './components/PreviewButton';
import useStyles from './LandingPage.styles';

/** Safe modulus operator that works on negative numbers as expected, unlike js % operator.
 * @param {number} x - number being divided
 * @param {number} m - dividend
 */
const mod = (x, m) => ((x % m) + m) % m;

const LandingPageLayout = (props) => {
  const {
    fieldsComponent,
    generate,
    documents,
    canManageDocuments,
    hasTeamPermissions,
    flaggedDocumentsIds,
    toggleFlagState,
    toggleExampleOpen,
    documentControlsOptions,
    filterSelectComponent,
    emptyComponent,
    errorMessage,
    config,
  } = props;

  const classes = useStyles();

  const [generationIndex, setGenerationIndex] = useState(0);

  const downloadSelected = (docs) => {
    const exporter = new LandingPageExporter();

    docs.forEach((generationObject) => {
      const {
        heading,
        subheading,
        benefit1,
        benefit1_description: description1,
        benefit2,
        benefit2_description: description2,
        benefit3,
        benefit3_description: description3,
        slogan,
      } = generationObject.content;

      exporter.addPage(
        heading,
        subheading,
        benefit1,
        description1,
        benefit2,
        description2,
        benefit3,
        description3,
        slogan,
      );
    });

    downloadAsFile(exporter.getString(), makeFileName('copysmith', 'landingPages'));
  };

  const handleDelete = async (deleteDocument, documentsLength) => {
    if (generationIndex + 1 === documentsLength) {
      setGenerationIndex(mod(generationIndex - 1, documentsLength));
    }
    await deleteDocument();
  };

  return (
    <>
      <div className={classes.inputs}>
        {fieldsComponent}
        <Notification
          open={!!errorMessage}
          type="error"
          text={errorMessage || 'Error'}
        />
        <div className={classes.generateWrap}>
          <GenerateButtonWithCap
            canManageDocuments={canManageDocuments}
            generateHandler={generate}
            documentsExist={documents.length > 0}
            numOriginalContent="4"
            creditDecrementAmount={config.creditCost}
          />
        </div>
      </div>
      <div className={classes.content}>
        <DocumentControls
          actions={documents.length === 0 ? [] : ['example']}
          disableSelect
          toggleExampleOpen={documents.length === 0 ? null : toggleExampleOpen}
          onAssign={documentControlsOptions.onAssign}
          canManageDocuments={documentControlsOptions.canManageDocuments}
          assignedToId={documentControlsOptions.assignedToId}
          assignedToError={documentControlsOptions.assignedToError}
          workflowStatusError={documentControlsOptions.workflowStatusError}
          workflowStatus={documentControlsOptions.workflowStatus}
          onFileChange={documentControlsOptions.onFileChange}
          isDocumentsFlagged={documentControlsOptions.isDocumentsFlagged}
        />
        <div className={classes.generationWrapper}>
          {filterSelectComponent}
          {documents.length > 0 ? (
            <>
              <div
                className={classes.docsCounter}
              >
                {mod(generationIndex, documents.length) + 1}
                {' of '}
                {documents.length}
                .
              </div>
              <div
                className={classes.docsNav}
              >
                <button
                  type="button"
                  className={classes.newButton}
                  onClick={() => setGenerationIndex(
                    mod(generationIndex - 1, documents.length),
                  )}
                >
                  ‹ Previous
                </button>

                <Button
                  className={classes.export}
                  onClick={() => downloadSelected(documents)}
                >
                  Export as CSV
                </Button>

                <button
                  type="button"
                  className={classes.newButton}
                  onClick={() => setGenerationIndex(
                    mod(generationIndex + 1, documents.length),
                  )}
                >
                  Next ›
                </button>
              </div>
              <DocumentView
                document={documents[generationIndex]}
                onFlag={toggleFlagState(documents[generationIndex]?._id)}
                onDelete={(deleteDocument) => handleDelete(deleteDocument, documents.length)}
                flagged={flaggedDocumentsIds.includes(documents[generationIndex]?._id)}
                wordCount={[]}
                options={{
                  canDelete: canManageDocuments,
                  canEdit: canManageDocuments,
                  canFlag: hasTeamPermissions,
                  canLike: true,
                  canCopy: false,
                  canPersonalized: true,
                  canSubmitExample: true,
                }}
                customFooterAction={(
                  <PreviewButton documentId={documents[generationIndex]?._id} />
                )}
              />
            </>
          ) : (
            emptyComponent
          )}
        </div>
      </div>
    </>
  );
};

export default LandingPageLayout;
